import { useCallback, useSyncExternalStore } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type NavigatorWithInstallApis = Navigator & {
  // iOS Safari: true when launched from the home screen.
  standalone?: boolean;
  // Chromium only; needs `related_applications` in the manifest.
  getInstalledRelatedApps?: () => Promise<unknown[]>;
};

export type InstallStatus =
  | "standalone" // running as the installed app
  | "installable" // browser offered beforeinstallprompt
  | "installed" // installed, viewed in a browser tab (Chromium only)
  | "guide" // Safari: manual install steps
  | "unsupported";

export type InstallGuide = "ios" | "macos-safari";

// The browser fires beforeinstallprompt once per page load, so it must be
// captured at startup (before Settings is lazily loaded) and kept here.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function captureInstallPrompt(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });

  (navigator as NavigatorWithInstallApis)
    .getInstalledRelatedApps?.()
    .then((apps) => {
      if (apps.length > 0) {
        installed = true;
        notify();
      }
    })
    .catch(() => {});
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as NavigatorWithInstallApis).standalone === true
  );
}

/** Safari platforms with no install prompt; null elsewhere. */
export function getInstallGuide(): InstallGuide | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  // iPadOS reports a Mac UA; touch support tells them apart.
  const isIos =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  const isSafari =
    /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Firefox\//.test(ua);
  if (/Macintosh/.test(ua) && isSafari) return "macos-safari";
  return null;
}

function getStatus(): InstallStatus {
  if (isStandalone()) return "standalone";
  if (installed) return "installed";
  if (deferredPrompt) return "installable";
  return getInstallGuide() ? "guide" : "unsupported";
}

export function useInstallPrompt(): {
  canInstall: boolean;
  status: InstallStatus;
  install: () => Promise<void>;
} {
  const status = useSyncExternalStore(
    subscribe,
    getStatus,
    (): InstallStatus => "unsupported"
  );

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    const prompt = deferredPrompt;
    await prompt.prompt();
    await prompt.userChoice;
    // A prompt event can only be used once.
    deferredPrompt = null;
    notify();
  }, []);

  return { canInstall: status === "installable", status, install };
}
